import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import BeatLoader from 'react-spinners/BeatLoader';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import { PlayIcon, CursorArrowRaysIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import LineChart from '../charts/LineChart02';
import { useClientSwitch } from '../hooks/useClientSwitch';
import { fetchGameDailyStats, fetchGameStats, getStatsCacheDuration } from '../services/gameAnalytics';
import { useUser } from '../userContext';
import FreshnessIndicator from '../components/FreshnessIndicator';
import { buildChartData } from '../utils/chartUtils';
import formatCompact from '../utils/formatCompact';

// Subscriptions retired: every workspace gets the full 90-day analytics window.
const ANALYTICS_LIMITS = {
  default: 90,
};

// Demo chart component for preview mode
const DemoChartComponent = () => {
  const [chartView, setChartView] = useState('today');
  const [visibleDatasets, setVisibleDatasets] = useState({
    'Plays': true,
    'CTA Clicks': true,
    'Entries': true
  });
  const [showHourlyDetail, setShowHourlyDetail] = useState(false);
  
  // Generate realistic demo data matching live chart structure exactly
  const generateDemoData = (view) => {
    if (view === 'today') {
      const labels = showHourlyDetail 
        ? ['12am', '1am', '2am', '3am', '4am', '5am', '6am', '7am', '8am', '9am', '10am', '11am',
           '12pm', '1pm', '2pm', '3pm', '4pm', '5pm', '6pm', '7pm', '8pm', '9pm', '10pm', '11pm']
        : ['12am-4am', '4am-8am', '8am-12pm', '12pm-4pm', '4pm-8pm', '8pm-12am'];
      
      const playData = showHourlyDetail
        ? [0, 0, 0, 0, 0, 1, 2, 5, 8, 12, 15, 11, 9, 14, 18, 16, 13, 10, 8, 6, 4, 3, 2, 1]
        : [1, 8, 46, 57, 37, 10];
      
      const ctaData = showHourlyDetail
        ? [0, 0, 0, 0, 0, 0, 1, 1, 2, 3, 4, 3, 2, 4, 5, 4, 3, 3, 2, 2, 1, 1, 0, 0]
        : [0, 2, 12, 15, 10, 2];
      
      const leadData = showHourlyDetail
        ? [0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 2, 2, 1, 2, 2, 2, 1, 1, 1, 1, 0, 0, 0, 0]
        : [0, 1, 5, 6, 4, 1];

      return { labels, playData, ctaData, leadData };
    } else {
      // 7 days: use ISO date strings so x-axis tick callback formats like dashboard (month short, day numeric)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const last7 = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(today);
        d.setDate(today.getDate() - (6 - i));
        return d.toISOString().split('T')[0];
      });
      return {
        labels: last7,
        playData: [45, 62, 38, 71, 89, 56, 78],
        ctaData: [12, 15, 10, 18, 22, 14, 19],
        leadData: [7, 9, 6, 11, 13, 8, 12]
      };
    }
  };

  const { labels, playData, ctaData, leadData } = generateDemoData(chartView);
  
  const demoChartData = {
    labels,
    datasets: [
      {
        label: 'Plays',
        data: playData,
        borderColor: '#9B59B6',
        backgroundColor: 'rgba(155, 89, 182, 0.2)',
        fill: true,
        borderWidth: 3,
        tension: 0.3,
        pointRadius: 0,
        hidden: !visibleDatasets['Plays']
      },
      {
        label: 'CTA Clicks',
        data: ctaData,
        borderColor: '#3498DB',
        backgroundColor: 'rgba(52, 152, 219, 0.2)',
        fill: true,
        borderWidth: 3,
        tension: 0.3,
        pointRadius: 0,
        hidden: !visibleDatasets['CTA Clicks']
      },
      {
        label: 'Entries',
        data: leadData,
        borderColor: '#16A34A',
        backgroundColor: 'rgba(22, 163, 74, 0.2)',
        fill: true,
        borderWidth: 3,
        tension: 0.3,
        pointRadius: 0,
        hidden: !visibleDatasets['Entries']
      }
    ]
  };

  const chartTotals = {
    plays: playData.reduce((a, b) => a + b, 0),
    ctas: ctaData.reduce((a, b) => a + b, 0),
    leads: leadData.reduce((a, b) => a + b, 0)
  };

  // Fixed overall stats
  const overallPlays = 347;
  const overallCTAs = 89;
  const overallLeads = 58;
  const demoStats = [
    { name: "Total Plays", shortName: "Plays", overall: overallPlays, change: chartTotals.plays },
    { name: "CTA Clicks", shortName: "CTA", overall: overallCTAs, change: chartTotals.ctas },
    { name: "Entries", shortName: "Entries", overall: overallLeads, change: chartTotals.leads }
  ];

  // Trend indicators removed - cumulative totals don't need trends

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <div className="rounded-xl overflow-hidden">
        <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-5">
          {demoStats.map((stat, index) => (
            <motion.div 
              className="gch-card p-4 sm:p-4" 
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <p className="text-xs sm:text-sm font-bold uppercase text-center sm:text-left gch-text-secondary">
                <span className="block sm:hidden">{stat.shortName}</span>
                <span className="hidden sm:block">{stat.name}</span>
              </p>
              <div className="flex justify-center sm:justify-start items-center mt-1 sm:mt-2">
                <p className="text-xl sm:text-3xl font-black leading-none gch-text-primary">
                  {formatCompact(stat.overall)}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <ChartContent
        chartData={demoChartData}
        chartView={chartView}
        onChartViewChange={setChartView}
        isChartSwitching={false}

        showHourlyDetail={false}
        onHourlyDetailChange={() => {}}
        visibleDatasets={visibleDatasets}
        onVisibleDatasetsChange={setVisibleDatasets}
        chartTotals={chartTotals}
        hideToggles
      />
    </motion.div>
  );
};

function Tooltip({ children, text }) {
  return (
    <div className="group relative inline-block">
      {children}
      <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-9 opacity-0 group-hover:opacity-100">
        <div className="bg-gray-900 text-white text-sm px-2 py-1 rounded whitespace-nowrap" style={{fontFamily: '"Inter", sans-serif'}}>
          {text}
          <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900"></div>
        </div>
      </div>
    </div>
  );
}

function StatsSkeleton() {
  return (
    <>
      <div className="rounded-xl overflow-hidden">
        <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-5">
          {[0, 1, 2].map((index) => (
            <div 
              key={index}
              className="gch-card p-4 sm:p-4 transition-all duration-300"
            >
              <div className="h-4 rounded w-2/3 mb-3 animate-pulse bg-white/10" />
              <div className="flex items-center">
                <div className="h-8 rounded w-1/2 animate-pulse bg-white/10" />
                <div className="h-6 rounded-full w-12 ml-2 animate-pulse bg-white/10" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-6 gch-card p-4">
        <div className="h-[300px] relative">
          <div className="absolute left-0 top-0 bottom-0 w-px bg-white/10" />
          <div className="absolute left-0 bottom-0 right-0 h-px bg-white/10" />
          <div className="absolute inset-0 flex items-end justify-between px-4">
            {[...Array(7)].map((_, i) => (
              <motion.div
                key={i}
                className="w-1 rounded-t animate-pulse bg-white/20"
                initial={{ height: "20%" }}
                animate={{ height: `${20 + Math.random() * 50}%` }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function EmptyState({ onSetLive }) {
  return (
    <div className="mt-4">
      {/* Preview Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {[
          { title: 'Total Plays', description: 'Number of game plays', icon: PlayIcon },
          { title: 'Completions', description: 'Plays that reached the end', icon: CheckCircleIcon },
          { title: 'CTA clicks', description: 'CTA button clicks from players', icon: CursorArrowRaysIcon },
        ].map((metric, index) => (
          <div 
            key={index}
            className="gch-card p-4 sm:p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold gch-text-primary">
                {metric.title}
              </h4>
              <div className="h-7 w-7 rounded-full flex items-center justify-center gch-input-ghost">
                <metric.icon className="w-4 h-4 gch-text-secondary" />
              </div>
            </div>
            <p className="text-xs font-medium gch-text-secondary">
              {metric.description}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <div className="gch-card p-4">
          <div className="h-[300px] relative">
            {/* Grid lines */}
            <div className="absolute inset-0 grid grid-cols-6 gap-x-4">
              {[...Array(7)].map((_, i) => (
                <div key={i} className="h-full" style={{borderLeft: '1px solid rgba(148, 163, 184, 0.1)'}} />
              ))}
            </div>
            <div className="absolute inset-0 grid grid-rows-4 gap-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-full" style={{borderBottom: '1px solid rgba(148, 163, 184, 0.1)'}} />
              ))}
            </div>

            {/* Example data points */}
            <div className="absolute inset-x-0 bottom-8 h-3/4">
              <svg className="w-full h-full" preserveAspectRatio="none">
                <path
                  d="M 0 160 C 100 120, 200 180, 300 60"
                  fill="none"
                  strokeWidth="2"
                  strokeLinecap="round"
                  style={{stroke: 'rgba(99, 102, 241, 0.3)'}}
                />
              </svg>
            </div>

            <div className="absolute inset-0 flex items-center justify-center bg-[var(--admin-bg-surface)]/95">
              <div className="text-center space-y-3">
                <button
                  onClick={() => onSetLive()}
                  className="btn-admin-primary btn-admin-md inline-flex items-center"
                >
                  <span className="mr-2">▶</span>
                  Activate Game
                </button>
                <p className="text-xs font-semibold gch-text-secondary">
                  Stats will display when game is active
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChartContent({
  chartData,
  chartView,
  onChartViewChange,
  isChartSwitching,

  showHourlyDetail,
  onHourlyDetailChange,
  visibleDatasets,
  onVisibleDatasetsChange,
  chartTotals,
  updatedAt,
  onRefresh,
  isLoading = false,
  noCardWrapper = false,
  hideToggles = false,
  subscriptionStatus,
  openUpgradeModal,
  showExtendedRanges = false
}) {
  const isPaidPlan = ['active', 'trialing'].includes(subscriptionStatus);
  // Chart options aligned with DashboardChart (same labels, tooltips, scales)
  const chartOptions = {
    animation: { duration: 0 },
    scales: {
      x: {
        type: 'category',
        ticks: {
          autoSkip: true,
          maxTicksLimit: chartView === 'today' && showHourlyDetail ? 6 : (chartView === '90days' ? 6 : (chartView === '30days' ? 6 : undefined)),
          maxRotation: 0,
          color: '#B8C5D0',
          callback: ['7days', '30days', '90days'].includes(chartView) ? function(value, index) {
            const labelValue = this.getLabelForValue(value);
            const date = new Date(labelValue);
            if (isNaN(date.getTime())) return labelValue;
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } : function(value, index) {
            // Today view: show every 4th hour only (6 labels) to avoid crowding  - same as dashboard
            const label = this.getLabelForValue(value);
            if (showHourlyDetail && this.chart?.data?.labels?.length === 24) {
              return index % 4 === 0 ? label : '';
            }
            return label;
          },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        min: 0,
        suggestedMin: 0,
        suggestedMax: 10,
        ticks: {
          maxTicksLimit: 5,
          color: '#B8C5D0',
          callback: (value) => Math.round(value),
        },
        grid: { color: 'rgba(184, 197, 208, 0.2)' },
        afterDataLimits: (scale) => {
          if (scale.max < 10) scale.max = 10;
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function(context) {
            if (['7days', '30days'].includes(chartView)) {
              const dateStr = context[0].label;
              const date = new Date(dateStr);
              if (isNaN(date.getTime())) return dateStr;
              return date.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              });
            }
            if (chartView === '90days') {
              return `Week of ${context[0].label}`;
            }
            return context[0].label;
          },
          label: (context) => `${context.dataset.label || ''}: ${context.parsed.y}`,
        },
      },
    },
    layout: { padding: { left: 8, right: 16, top: 10, bottom: 20 } },
    interaction: { mode: 'index', intersect: false },
    elements: { line: { tension: 0.3, borderWidth: 2 }, point: { radius: 0 } },
    maintainAspectRatio: false,
    resizeDelay: 200,
  };

  const content = (
    <>
        <header className={noCardWrapper ? 'px-0 pt-0 pb-3 border-b border-[var(--admin-border)]' : 'px-5 py-4 border-b border-[var(--admin-border)]'}>
          <div className="flex flex-wrap justify-between items-center gap-2">
            <div>
              <h2 className="text-xs font-semibold uppercase tracking-wide gch-text-secondary">
                Recent activity
              </h2>
              {updatedAt && (
                <div className="mt-0.5">
                  <FreshnessIndicator timestamp={updatedAt} onRefresh={onRefresh} />
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex flex-wrap rounded-lg p-1 gch-bg-card border border-[var(--admin-border)]">
                <button
                  onClick={() => onChartViewChange('today')}
                  className={`px-2 sm:px-3 py-1 text-[11px] sm:text-xs font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${
                    chartView === 'today'
                      ? 'bg-[var(--admin-cta-solid)] text-white'
                      : 'gch-text-secondary hover:bg-white/10'
                  }`}
                >
                  Today
                </button>
                <button
                  onClick={() => onChartViewChange('7days')}
                  className={`px-2 sm:px-3 py-1 text-[11px] sm:text-xs font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${
                    chartView === '7days'
                      ? 'bg-[var(--admin-cta-solid)] text-white'
                      : 'gch-text-secondary hover:bg-white/10'
                  }`}
                >
                  7 days
                </button>
                {showExtendedRanges && (
                <button
                  onClick={() => onChartViewChange('30days')}
                  className={`px-2 sm:px-3 py-1 text-[11px] sm:text-xs font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${
                    chartView === '30days'
                      ? 'bg-[var(--admin-cta-solid)] text-white'
                      : 'gch-text-secondary hover:bg-white/10'
                  }`}
                >
                  30 days
                </button>
                )}
                {showExtendedRanges && (
                <button
                  onClick={() => onChartViewChange('90days')}
                  className={`px-2 sm:px-3 py-1 text-[11px] sm:text-xs font-semibold rounded-md transition-all duration-200 whitespace-nowrap ${
                    chartView === '90days'
                      ? 'bg-[var(--admin-cta-solid)] text-white'
                      : 'gch-text-secondary hover:bg-white/10'
                  }`}
                >
                  90 days
                </button>
                )}
              </div>
            </div>
          </div>
        </header>
        {!hideToggles && (
        <div className={`pt-4 pb-2 flex gap-2 flex-wrap items-center ${noCardWrapper ? 'px-0' : 'px-5'}`}>
          <button
            onClick={() => onVisibleDatasetsChange(prev => ({ ...prev, 'Plays': !prev['Plays'] }))}
            className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
              visibleDatasets['Plays']
                ? 'bg-purple-500/20 border border-purple-400/40 text-purple-200'
                : 'gch-input-ghost gch-text-secondary'
            }`}
          >
            <span className="w-2 h-2 rounded-full mr-2 bg-purple-400"></span>
            <span className="font-bold">{formatCompact(chartTotals.plays)}</span>
            <span className="ml-1">Plays</span>
          </button>

          <button
            onClick={() => onVisibleDatasetsChange(prev => ({ ...prev, 'CTA Clicks': !prev['CTA Clicks'] }))}
            className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
              visibleDatasets['CTA Clicks']
                ? 'bg-blue-400/20 border border-blue-400/40 text-blue-200'
                : 'gch-input-ghost gch-text-secondary'
            }`}
          >
            <span className="w-2 h-2 rounded-full mr-2 bg-blue-400"></span>
            <span className="font-bold">{formatCompact(chartTotals.ctas || 0)}</span>
            <span className="ml-1">Link clicks</span>
          </button>

          <button
            onClick={() => onVisibleDatasetsChange(prev => ({ ...prev, 'Entries': !prev['Entries'] }))}
            className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
              visibleDatasets['Entries']
                ? 'bg-green-400/20 border border-green-400/40 text-green-200'
                : 'gch-input-ghost gch-text-secondary'
            }`}
          >
            <span className="w-2 h-2 rounded-full mr-2 bg-green-400"></span>
            <span className="font-bold">{formatCompact(chartTotals.leads || 0)}</span>
            <span className="ml-1">Entries</span>
          </button>


        </div>
        )}

        {/* Chart or empty state for current view */}
        <div className={noCardWrapper ? 'p-0 pt-4' : 'p-4 pl-4 pr-5'}>
          {chartData?.datasets?.some(ds => Array.isArray(ds?.data) && ds.data.some(val => val > 0)) ? (
            <LineChart
              key={`${chartView}-${chartData?.labels?.length || 0}-${chartData?.labels?.[0] || ''}`}
              data={chartData}
              width={595}
              height={248}
              options={chartOptions}
            />
          ) : (
            <div className="flex flex-col justify-center items-center py-10 px-6 min-h-[200px] text-center">
              <h3 className="text-sm font-semibold mb-2 tracking-tight gch-text-primary">
                {chartView === 'today' ? 'No plays today yet'
                  : chartView === '7days' ? 'No plays in the last 7 days'
                  : chartView === '30days' ? 'No plays in the last 30 days'
                  : chartView === '90days' ? 'No plays in the last 90 days'
                  : 'No plays yet'}
              </h3>
              <p className="text-xs font-medium gch-text-secondary max-w-xs">
                {hideToggles
                  ? 'Plays will appear here once your campaign gets traffic.'
                  : 'Copy your share link or download the QR code from the panel on the right to get your first play.'}
              </p>
              {!hideToggles && chartView === 'today' && (
                <p className="text-xs gch-text-muted mt-2">
                  Try the <strong>7 days</strong> view above for a wider window.
                </p>
              )}
            </div>
          )}
        </div>
    </>
  );

  return noCardWrapper ? content : (
    <div className="relative">
      <div className="gch-card overflow-hidden">{content}</div>
    </div>
  );
}

// Component-level caching removed in Phase 3 — the service layer (gameAnalytics.js)
// is the single cache authority with one adaptive TTL curve and one invalidation path.

export { StatsSkeleton, ChartContent };

export default function Stats({ campaignIds, campaign, insertBetweenCardsAndChart, hideStatCards = false, onStatsForSidebar, layout, prefetchedStats, prefetchedDailyStats, lastUpdated: externalLastUpdated, onRefresh: externalOnRefresh }) {
  const isPrefetched = prefetchedStats != null && prefetchedDailyStats != null;
  // Subscriptions retired: subscriptionStatus is always null and the upgrade modal is a no-op.
  const subscriptionStatus = null;
  const openUpgradeModal = () => {};
  // Keep useUser hook called for future hooks; no destructured fields needed today.
  useUser();
  const [stats, setStats] = useState([]);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });
  const [isLoading, setIsLoading] = useState(!isPrefetched);
  const [isChartLoading, setIsChartLoading] = useState(!isPrefetched);
  const [isChartUpdating, setIsChartUpdating] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const chartRangeKey = campaignIds?.[0] ? `sf_chart_range_${campaignIds[0]}` : null;
  const [chartView, setChartView] = useState(() => {
    if (!chartRangeKey) return 'today';
    const saved = localStorage.getItem(chartRangeKey);
    return ['today', '7days', '30days', '90days'].includes(saved) ? saved : 'today';
  });

  // Check if account is paused (limited analytics access)
  const isPaused = subscriptionStatus === 'paused' || subscriptionStatus === 'canceled';
  const isPaidPlan = ['active', 'trialing'].includes(subscriptionStatus);
  const analyticsLimit = ANALYTICS_LIMITS[subscriptionStatus] || ANALYTICS_LIMITS.default;

  // Only show 30/90-day tabs when the campaign has been live long enough for them to be useful
  const showExtendedRanges = useMemo(() => {
    const published = campaign?.publishedAt;
    if (!published) return false;
    const publishedDate = typeof published === 'string' ? new Date(published) : published?.toDate ? published.toDate() : new Date(published);
    return (Date.now() - publishedDate.getTime()) > 7 * 24 * 60 * 60 * 1000;
  }, [campaign?.publishedAt]);

  // If saved view requires paid plan or extended range isn't available, fall back
  useEffect(() => {
    if ((!isPaidPlan || !showExtendedRanges) && ['30days', '90days'].includes(chartView)) {
      setChartView('7days');
    }
  }, [isPaidPlan, showExtendedRanges]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showHourlyDetail, setShowHourlyDetail] = useState(true); // Today view: hourly labels (12am–11pm) by default for clarity
  const [isChartSwitching, setIsChartSwitching] = useState(false);
  const [visibleDatasets, setVisibleDatasets] = useState({
    'Plays': true,
    'CTA Clicks': true,
    'Entries': true
  });
  const [chartTotals, setChartTotals] = useState({ plays: 0, ctas: 0, leads: 0 });
  const [autoRefreshMs, setAutoRefreshMs] = useState(30000);
  const [statChangeFlags, setStatChangeFlags] = useState({});
  const [lastStatsUpdate, setLastStatsUpdate] = useState(null);
  /** Lifetime totals for sidebar Overview (not today/7day period) */
  const [sidebarLifetimeStats, setSidebarLifetimeStats] = useState(null);
  const chartTimeoutRef = useRef(null);
  const hasFetchedData = useRef(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const prevStatsRef = useRef([]);
  const statChangeTimersRef = useRef({});
  const forceRefreshRef = useRef(false);

  // Handler for chart view changes with loading state
  const handleChartViewChange = async (newView) => {
    if (newView === chartView) return;

    setIsChartSwitching(true);
    if (chartTimeoutRef.current) {
      clearTimeout(chartTimeoutRef.current);
    }
    chartTimeoutRef.current = setTimeout(() => {
      setIsChartSwitching(false);
      chartTimeoutRef.current = null;
    }, 1000);

    // Small delay to show loading state
    await new Promise(resolve => setTimeout(resolve, 100));

    setChartView(newView);
    if (chartRangeKey) localStorage.setItem(chartRangeKey, newView);
  };

  const handleForceRefresh = useCallback(() => {
    forceRefreshRef.current = true;
    setRefreshKey(prev => prev + 1);
  }, []);

  // utcHourlyToLocal and buildChartData imported from utils/chartUtils

  const MIN_AUTO_REFRESH_MS = 10000; // avoid setInterval(..., 0) runaway loop

  // Auto-refresh interval — skip when parent provides prefetched data (parent handles refresh)
  useEffect(() => {
    if (isPrefetched) return;
    if (!campaignIds || campaignIds.length === 0 || campaignIds.some(id => !id)) return;

    const delay = Math.max(autoRefreshMs, MIN_AUTO_REFRESH_MS);
    const interval = setInterval(() => {
      if (document.visibilityState !== 'hidden') {
        setRefreshKey(prev => prev + 1);
      }
    }, delay);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        setRefreshKey(prev => prev + 1);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isPrefetched, campaignIds, autoRefreshMs]);

  useEffect(() => {
    async function fetchStatsWithSampling() {
      try {
        setIsLoading(true);

        const shouldForceRefresh = forceRefreshRef.current;
        forceRefreshRef.current = false;

        // Fetch stats using gameAnalytics service (service handles caching)
        const gameStatsResults = await Promise.all(
          campaignIds.map(id => fetchGameStats(id, { forceRefresh: shouldForceRefresh }))
        );
        
        // Aggregate stats from all games
        const aggregatedStats = gameStatsResults.reduce((acc, stats) => ({
          totalPlays: acc.totalPlays + stats.totalPlays,
          totalCompletions: acc.totalCompletions + stats.totalCompletions,
          totalCtaClicks: acc.totalCtaClicks + stats.totalCtas,
          totalShares: acc.totalShares + (stats.totalShares || 0),
          totalLeaderboardJoins: acc.totalLeaderboardJoins + (stats.totalLeaderboardJoins || 0),
          avgDurationSeconds: acc.avgDurationSeconds + (stats.avgDurationSeconds || 0),
          devices: {
            mobile: acc.devices.mobile + (stats.devices?.mobile || 0),
            desktop: acc.devices.desktop + (stats.devices?.desktop || 0),
            tablet: acc.devices.tablet + (stats.devices?.tablet || 0),
          },
        }), { totalPlays: 0, totalCompletions: 0, totalCtaClicks: 0, totalShares: 0, totalLeaderboardJoins: 0, avgDurationSeconds: 0, devices: { mobile: 0, desktop: 0, tablet: 0 } });
        // Derive rates from aggregated totals
        aggregatedStats.completionRate = aggregatedStats.totalPlays > 0 ? Math.round((aggregatedStats.totalCompletions / aggregatedStats.totalPlays) * 100) : 0;
        aggregatedStats.shareRate = aggregatedStats.totalCompletions > 0 ? Math.round((aggregatedStats.totalShares / aggregatedStats.totalCompletions) * 100) : 0;
        aggregatedStats.ctaRate = aggregatedStats.totalCompletions > 0 ? Math.round((aggregatedStats.totalCtaClicks / aggregatedStats.totalCompletions) * 100) : 0;
        // Average duration across campaigns (simple average)
        if (gameStatsResults.length > 1) {
          const durations = gameStatsResults.filter(s => s.avgDurationSeconds > 0);
          aggregatedStats.avgDurationSeconds = durations.length > 0 ? Math.round(durations.reduce((s, d) => s + d.avgDurationSeconds, 0) / durations.length) : 0;
        }
        
        const dailyResults = await Promise.all(
          campaignIds.map(id => fetchGameDailyStats(id, analyticsLimit, { forceRefresh: shouldForceRefresh }))
        );

        const dailyMap = new Map();
        dailyResults.forEach(list => {
          list.forEach(entry => {
            const existing = dailyMap.get(entry.date) || {
              date: entry.date,
              plays: 0,
              completions: 0,
              ctas: 0,
              leads: 0,
              hourlyPlays: new Array(24).fill(0),
              hourlyCtas: new Array(24).fill(0),
              hourlyLeads: new Array(24).fill(0)
            };
            existing.plays += entry.plays || 0;
            existing.completions += entry.completions || 0;
            existing.ctas += entry.ctas || 0;
            existing.leads += entry.leaderboardJoins || 0;
            for (let i = 0; i < 24; i++) {
              existing.hourlyPlays[i] += entry.hourlyPlays?.[i] || 0;
              existing.hourlyCtas[i] += entry.hourlyCtas?.[i] || 0;
              existing.hourlyLeads[i] += entry.hourlyLeads?.[i] || 0;
            }
            dailyMap.set(entry.date, existing);
          });
        });

        const aggregatedDaily = Array.from(dailyMap.values()).sort((a, b) => a.date.localeCompare(b.date));

        // Aggregate traffic sources from daily stats
        const referrerCounts = {};
        const utmCounts = {};
        dailyResults.forEach(list => {
          list.forEach(entry => {
            (entry.topReferrers || []).forEach(r => { if (r) referrerCounts[r] = (referrerCounts[r] || 0) + 1; });
            (entry.topUtmSources || []).forEach(u => { if (u) utmCounts[u] = (utmCounts[u] || 0) + 1; });
          });
        });
        const isSelfReferrer = (r) => /^(direct|localhost|.*smilesflow\..*)$/i.test(r);
        aggregatedStats.topReferrers = Object.entries(referrerCounts).filter(([name]) => !isSelfReferrer(name)).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name]) => name);
        aggregatedStats.topUtmSources = Object.entries(utmCounts).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([name]) => name);

        // Lifetime totals for sidebar Overview (all time, from aggregated game stats)
        setSidebarLifetimeStats(aggregatedStats);

        const buildViewPayload = (view) => {
          const { playCounts, ctaCounts, leadCounts, chartLabels } = buildChartData(aggregatedDaily, view, showHourlyDetail);
          const todayKey = new Date().toISOString().split('T')[0];
          const periodStats = view === 'today'
            ? (aggregatedDaily.find(d => d.date === todayKey) || { plays: 0, completions: 0, ctas: 0, leads: 0 })
            : aggregatedDaily.reduce((acc, d) => ({
                plays: acc.plays + (d.plays || 0),
                completions: acc.completions + (d.completions || 0),
                ctas: acc.ctas + (d.ctas || 0),
                leads: acc.leads + (d.leads || 0)
              }), { plays: 0, completions: 0, ctas: 0, leads: 0 });

          const statComparison = [
            { 
              name: 'Total Plays', 
              shortName: 'Plays', 
              overall: periodStats.plays, 
              today: 0, 
              change: 0
            },
            {
              name: 'Link clicks',
              shortName: 'CTA',
              overall: periodStats.ctas,
              today: 0,
              change: 0
            },
            { 
              name: 'Entries', 
              shortName: 'Entries', 
              overall: periodStats.leads, 
              today: 0, 
              change: 0
            },
          ];

          const formattedChartData = {
            labels: chartLabels,
            datasets: [
              {
                label: 'Plays',
                data: playCounts,
                borderColor: '#9B59B6',
                backgroundColor: 'rgba(155, 89, 182, 0.2)',
                fill: true,
                borderWidth: 3,
                tension: 0.3,
                pointRadius: 0,
                pointHoverRadius: 5,
                pointBackgroundColor: '#9B59B6',
                pointHoverBackgroundColor: '#F1C40F',
                pointBorderWidth: 0,
                pointHoverBorderWidth: 2,
                pointHoverBorderColor: '#E67E22',
                clip: 20,
              },
            ]
          };

          const calculatedTotals = {
            plays: playCounts.reduce((sum, val) => sum + val, 0),
            ctas: ctaCounts.reduce((sum, val) => sum + val, 0),
            leads: leadCounts.reduce((sum, val) => sum + val, 0)
          };

          return { statComparison, formattedChartData, calculatedTotals, periodPlays: periodStats.plays };
        };

        const { statComparison, formattedChartData, calculatedTotals, periodPlays } = buildViewPayload(chartView);

        setStats(statComparison);
        setAutoRefreshMs(getStatsCacheDuration(periodPlays) || 30000);
        
        setChartTotals(calculatedTotals);
        
        // Apply dataset visibility
        const chartDataWithVisibility = {
          ...formattedChartData,
          datasets: formattedChartData.datasets.map(dataset => ({
            ...dataset,
            hidden: !visibleDatasets[dataset.label]
          }))
        };
        
        setChartData(chartDataWithVisibility);

        setLastStatsUpdate(Date.now());

        hasFetchedData.current = true;
      } catch (error) {
        console.error('Error fetching stats:', error);
        // Set empty chart data on error; do not update lastStatsUpdate so "Updated" stays last successful time
        setChartData({
          labels: [],
          datasets: []
        });
        setStats([]);
        setSidebarLifetimeStats(null);
      } finally {
        setIsLoading(false);
        setIsChartLoading(false);
      }
    }

    if (isPrefetched) return; // Parent provides data — handled by prefetched effect below
    if (campaignIds && campaignIds.length > 0 && campaignIds.every(id => !!id)) {
      fetchStatsWithSampling();
    } else {
      // Set empty data when no campaigns
      setChartData({
        labels: [],
        datasets: []
      });
      setStats([]);
      setSidebarLifetimeStats(null);
      setIsLoading(false);
      setIsChartLoading(false);
    }
  }, [isPrefetched, campaignIds, chartView, showHourlyDetail, refreshKey]);

  // Process prefetched data from parent (Phase 4: parent owns the fetch)
  useEffect(() => {
    if (!isPrefetched) return;
    if (!prefetchedStats || !prefetchedDailyStats) return;

    const aggregatedStats = {
      totalPlays: prefetchedStats.totalPlays || 0,
      totalCompletions: prefetchedStats.totalCompletions || 0,
      totalCtaClicks: prefetchedStats.totalCtas || 0,
      totalShares: prefetchedStats.totalShares || 0,
      totalLeaderboardJoins: prefetchedStats.totalLeaderboardJoins || 0,
      avgDurationSeconds: prefetchedStats.avgDurationSeconds || 0,
      devices: prefetchedStats.devices || { mobile: 0, desktop: 0, tablet: 0 },
    };
    aggregatedStats.completionRate = aggregatedStats.totalPlays > 0 ? Math.round((aggregatedStats.totalCompletions / aggregatedStats.totalPlays) * 100) : 0;
    aggregatedStats.shareRate = aggregatedStats.totalCompletions > 0 ? Math.round((aggregatedStats.totalShares / aggregatedStats.totalCompletions) * 100) : 0;
    aggregatedStats.ctaRate = aggregatedStats.totalCompletions > 0 ? Math.round((aggregatedStats.totalCtaClicks / aggregatedStats.totalCompletions) * 100) : 0;

    setSidebarLifetimeStats(aggregatedStats);

    // Build daily data for chart
    const dailyMap = new Map();
    prefetchedDailyStats.forEach(entry => {
      const existing = dailyMap.get(entry.date) || {
        date: entry.date,
        plays: 0, completions: 0, ctas: 0, leads: 0,
        hourlyPlays: new Array(24).fill(0),
        hourlyCtas: new Array(24).fill(0),
        hourlyLeads: new Array(24).fill(0)
      };
      existing.plays += entry.plays || 0;
      existing.completions += entry.completions || 0;
      existing.ctas += entry.ctas || 0;
      existing.leads += entry.leaderboardJoins || 0;
      for (let i = 0; i < 24; i++) {
        existing.hourlyPlays[i] += entry.hourlyPlays?.[i] || 0;
        existing.hourlyCtas[i] += entry.hourlyCtas?.[i] || 0;
        existing.hourlyLeads[i] += entry.hourlyLeads?.[i] || 0;
      }
      dailyMap.set(entry.date, existing);
    });
    const aggregatedDaily = Array.from(dailyMap.values()).sort((a, b) => a.date.localeCompare(b.date));

    const { playCounts, ctaCounts, leadCounts, chartLabels } = buildChartData(aggregatedDaily, chartView, showHourlyDetail);
    const todayKey = new Date().toISOString().split('T')[0];
    const periodStats = chartView === 'today'
      ? (aggregatedDaily.find(d => d.date === todayKey) || { plays: 0, completions: 0, ctas: 0, leads: 0 })
      : aggregatedDaily.reduce((acc, d) => ({
          plays: acc.plays + (d.plays || 0),
          completions: acc.completions + (d.completions || 0),
          ctas: acc.ctas + (d.ctas || 0),
          leads: acc.leads + (d.leads || 0)
        }), { plays: 0, completions: 0, ctas: 0, leads: 0 });

    const statComparison = [
      { name: 'Total Plays', shortName: 'Plays', overall: periodStats.plays, today: 0, change: 0 },
      { name: 'Link clicks', shortName: 'CTA', overall: periodStats.ctas, today: 0, change: 0 },
      { name: 'Entries', shortName: 'Entries', overall: periodStats.leads, today: 0, change: 0 },
    ];

    const formattedChartData = {
      labels: chartLabels,
      datasets: [{
        label: 'Plays',
        data: playCounts,
        borderColor: '#9B59B6',
        backgroundColor: 'rgba(155, 89, 182, 0.2)',
        fill: true,
        borderWidth: 3,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointBackgroundColor: '#9B59B6',
        pointHoverBackgroundColor: '#F1C40F',
        pointBorderWidth: 0,
        pointHoverBorderWidth: 2,
        pointHoverBorderColor: '#E67E22',
        clip: 20,
      }]
    };

    const chartDataWithVisibility = {
      ...formattedChartData,
      datasets: formattedChartData.datasets.map(dataset => ({
        ...dataset,
        hidden: !visibleDatasets[dataset.label]
      }))
    };

    setStats(statComparison);
    setChartData(chartDataWithVisibility);
    setChartTotals({
      plays: playCounts.reduce((sum, val) => sum + val, 0),
      ctas: ctaCounts.reduce((sum, val) => sum + val, 0),
      leads: leadCounts.reduce((sum, val) => sum + val, 0)
    });
    setLastStatsUpdate(externalLastUpdated || Date.now());
    hasFetchedData.current = true;
    setIsLoading(false);
    setIsChartLoading(false);
  }, [isPrefetched, prefetchedStats, prefetchedDailyStats, chartView, showHourlyDetail, externalLastUpdated]);

  // Update chart visibility when toggling datasets
  useEffect(() => {
    if (chartData && chartData.datasets) {
      setChartData(prev => ({
        ...prev,
        datasets: prev.datasets.map(dataset => ({
          ...dataset,
          hidden: !visibleDatasets[dataset.label]
        }))
      }));
    }
  }, [visibleDatasets]);

  useEffect(() => {
    if (!stats || stats.length === 0) {
      prevStatsRef.current = stats;
      return;
    }

    const prevMap = new Map(prevStatsRef.current.map(s => [s.name, s.overall]));
    const changed = {};

    stats.forEach(stat => {
      const prevVal = prevMap.get(stat.name);
      if (prevVal !== undefined && prevVal !== stat.overall) {
        changed[stat.name] = true;
        if (statChangeTimersRef.current[stat.name]) {
          clearTimeout(statChangeTimersRef.current[stat.name]);
        }
        statChangeTimersRef.current[stat.name] = setTimeout(() => {
          setStatChangeFlags(prev => {
            const next = { ...prev };
            delete next[stat.name];
            return next;
          });
          delete statChangeTimersRef.current[stat.name];
        }, 5000);
      }
    });

    if (Object.keys(changed).length > 0) {
      setStatChangeFlags(prev => ({ ...prev, ...changed }));
    }

    prevStatsRef.current = stats;
  }, [stats]);

  useEffect(() => {
    if (sidebarLifetimeStats && Object.keys(sidebarLifetimeStats).length > 0 && onStatsForSidebar) {
      onStatsForSidebar(sidebarLifetimeStats, statChangeFlags, lastStatsUpdate);
    }
  }, [sidebarLifetimeStats, statChangeFlags, lastStatsUpdate, onStatsForSidebar]);

  useEffect(() => {
    return () => {
      Object.values(statChangeTimersRef.current).forEach(timer => clearTimeout(timer));
      statChangeTimersRef.current = {};
    };
  }, []);

  // Trend indicators removed - cumulative totals don't need trends

  if ((isLoading || isChartLoading) && !hasFetchedData.current) {
    return layout === 'gridCard' ? <div className="h-64 flex items-center justify-center gch-text-secondary text-sm">Loading...</div> : <StatsSkeleton />;
  }

  const chartBlock = (
    <AnimatePresence mode="wait">
      {isChartUpdating || isTransitioning ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full h-56 flex justify-center items-center"
        >
          <div className="text-center py-8 px-6 text-xs font-medium gch-text-secondary">
            <span className="animate-spin">⚙️</span> Loading chart data...
          </div>
        </motion.div>
      ) : (
          <ChartContent
            chartData={chartData}
            chartView={chartView}
            onChartViewChange={(newView) => handleChartViewChange(newView)}
            isChartSwitching={isChartSwitching}

            showHourlyDetail={showHourlyDetail}
            onHourlyDetailChange={() => setShowHourlyDetail(prev => !prev)}
            visibleDatasets={visibleDatasets}
            onVisibleDatasetsChange={setVisibleDatasets}
            chartTotals={chartTotals}
            updatedAt={lastStatsUpdate}
            onRefresh={isPrefetched ? externalOnRefresh : handleForceRefresh}
            isLoading={isLoading}
            noCardWrapper={layout === 'gridCard'}
            subscriptionStatus={subscriptionStatus}
            openUpgradeModal={openUpgradeModal}
            showExtendedRanges={showExtendedRanges}
            hideToggles
          />
        )}
    </AnimatePresence>
  );

  if (layout === 'gridCard') {
    return chartBlock;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="stats"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
      >
        {/* Paused Account Warning Banner */}
        {isPaused && (
          <div className="mb-4 p-3 gch-card flex items-start gap-3 border-l-4 border-l-[var(--admin-accent)]">
            <LockClosedIcon className="w-5 h-5 text-[var(--admin-accent)] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold gch-text-primary">Limited Analytics Access</p>
              <p className="text-xs gch-text-secondary mt-0.5">
                Your campaigns are paused. Analytics are limited to the last {analyticsLimit} days.{' '}
                <a href="/studio/plan" className="underline font-medium text-[var(--admin-accent)] hover:gch-text-primary">Buy a publish credit</a> to ship them again.
              </p>
            </div>
          </div>
        )}

        <div className={`rounded-xl overflow-hidden flex flex-col md:flex-row md:items-start md:gap-6${!hideStatCards || insertBetweenCardsAndChart ? ' mt-6' : ''}`}>
          {/* Main content: updated row, GET PLAYERS slot, chart */}
          <div className="flex-1 min-w-0 order-2 md:order-1">
            {insertBetweenCardsAndChart}

            <AnimatePresence mode="wait">
          {isChartUpdating || isTransitioning ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full h-56 gch-card p-4 flex justify-center items-center"
            >
              <div className="text-center py-8 px-6">
                <div className="flex items-center justify-center gap-2 text-xs font-medium gch-text-secondary">
                  <span className="animate-spin">⚙️</span>
                  <span>Loading chart data...</span>
                </div>
              </div>
            </motion.div>
          ) : (
              <ChartContent
                chartData={chartData}
                chartView={chartView}
                onChartViewChange={(newView) => handleChartViewChange(newView)}
                isChartSwitching={isChartSwitching}

                showHourlyDetail={showHourlyDetail}
                onHourlyDetailChange={() => setShowHourlyDetail(prev => !prev)}
                visibleDatasets={visibleDatasets}
                onVisibleDatasetsChange={setVisibleDatasets}
                chartTotals={chartTotals}
                updatedAt={lastStatsUpdate}
                onRefresh={isPrefetched ? externalOnRefresh : handleForceRefresh}
                isLoading={isLoading}
                subscriptionStatus={subscriptionStatus}
                openUpgradeModal={openUpgradeModal}
                showExtendedRanges={showExtendedRanges}
                hideToggles
              />
            )}
            </AnimatePresence>
          </div>

          {/* Stat counters: subtle, small (hidden when rendered in page sidebar via hideStatCards) */}
          {!hideStatCards && (
            <div className="flex flex-row justify-end gap-2 md:gap-0 md:flex-col md:w-28 flex-shrink-0 order-1 md:order-2 md:space-y-0.5 pb-2 md:pb-0">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.name}
                  className="rounded-md border border-white/10 bg-white/[0.03] px-2 py-1.5 flex items-center justify-between gap-2"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.04 }}
                >
                  <span className="text-[9px] font-medium uppercase tracking-wide gch-text-muted">{stat.name}</span>
                  <span className="text-xs tabular-nums gch-text-secondary">
                    {stat.isPercent ? `${stat.overall}%` : formatCompact(stat.overall)}
                    {statChangeFlags[stat.name] && <span className="text-[8px] gch-text-muted ml-0.5" aria-hidden="true" title="Updated">{">>"}</span>}
                  </span>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
